import React, { useState, useEffect } from 'react'
import { useMachine } from '@xstate/react'
import Email from '@screens/WorkOrders/new/Email'
import Photos from '@screens/WorkOrders/new/Photos'
import Description from '@screens/WorkOrders/new/Description'
import Finished from '@screens/WorkOrders/new/Finished'
import { workOrderMachine } from '@machines/newWorkOrderMachine'
import { useCurrentUser } from '@context/CurrentUserContext'
import { useMenu } from '@context/MenuContext'
import { useIsFocused } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import useNavigation from '@hooks/useNavigation'

const Stack = createNativeStackNavigator()

export default function () {
  const [current, send] = useMachine(workOrderMachine)
  const { currentUser, userAuthenticated } = useCurrentUser()
  const [previousAuthState, setPreviousAuthState] = useState(() => userAuthenticated)
  const { closeMenu } = useMenu()
  const navigation = useNavigation()
  const isFocused = useIsFocused()
  const [isNavigatingToWorkOrderCreation, setIsNavigatingToWorkOrderCreation] = useState(false)

  useEffect(() => {
    navigation.setOptions({ tabBarStyle: { display: 'none' } })
  }, [])

  useEffect(() => {
    if (userAuthenticated === previousAuthState) return
    setPreviousAuthState(userAuthenticated)

    /**
     * If the user becomes unauthenticated, send them back to emailCollection
     * and reset context by executing LOGOUT
     *  */
    if (!userAuthenticated) {
      send('LOGOUT')
      send('RESET')
      navigation.navigate('emailCollection')
      return
    }

    /**
     * Otherwise, if the user becomes authenticated, and they are in the email page,
     * automatically handle next for them
     *  */
    if (current.value === 'emailCollection') {
      handleNext()
      return
    }

    closeMenu()
  }, [userAuthenticated])

  /**
   * If you happen to navigate back to the WO creation flow, and the Thank you
   * page is focused, then we want to move the user to 'attachPhotos'
   */
  useEffect(() => {
    if (!isFocused && !isNavigatingToWorkOrderCreation) {
      setIsNavigatingToWorkOrderCreation(true)
      return
    }
    if (!isFocused || !isNavigatingToWorkOrderCreation) return

    // Will get here only if isFocused && isNavigatingToWorkOrderCreation
    setIsNavigatingToWorkOrderCreation(false)
    if (current.value === 'workOrderSubmitted') handleNew()
  }, [isFocused])

  const handlePrevious = () => send('PREV')

  const handleNew = () => {
    send('UPDATE')
    send('NEW')
    navigation.navigate('attachPhotos')
  }

  const handleNext = (value?: any) => {
    send({ type: 'UPDATE', value })
    send('NEXT')
    switch (current.value) {
      case 'emailCollection':
        navigation.navigate('attachPhotos')
        break
      case 'attachPhotos':
        navigation.navigate('setDescription')
        break
      case 'setDescription':
        navigation.navigate('workOrderSubmitted')
        break
      default:
    }
  }

  const handleUpdate = (value: any) => {
    send({ type: 'UPDATE', value })
  }

  const stepProps = {
    onNext: handleNext,
    onNew: handleNew,
    onPrevious: handlePrevious,
    onUpdate: handleUpdate,
    context: current.context,
  }

  const EmailScreen = () => <Email {...stepProps} />
  const PhotosScreen = () => <Photos {...stepProps} />
  const DescriptionScreen = () => <Description {...stepProps} />
  const FinishedScreen = () => <Finished {...stepProps} />

  return (
    <Stack.Navigator
      initialRouteName={
        !currentUser?.jwtToken ? 'emailCollection' : 'attachPhotos'
      }
      screenOptions={{ headerShown: false }}
    >
      <Stack.Screen name='emailCollection' component={ EmailScreen } />
      <Stack.Screen name='attachPhotos' component={ PhotosScreen } />
      <Stack.Screen name='setDescription' component={ DescriptionScreen } />
      <Stack.Screen name='workOrderSubmitted' component={ FinishedScreen } />
    </Stack.Navigator>
  )
}
